import { Suspense, useEffect, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import {
  $initialAccountsStore,
  $initialCardsStore,
  $initialCurrenciesAvailableStore,
  $profileFeatureStore,
  $profileStore,
  $remoteConfigStore,
} from '@lib/store';
import { AuthApi } from '@shared/api';
import { AuthUtils, LocalAuthData } from '@shared/utils';
import { Public, Private } from '.';
import { PageContainer } from '@widgets';
import { Spinner } from '@shared/widgets';

export const Root = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const profileStore = useUnit($profileStore.store);
  const remoteConfigStore = useUnit($remoteConfigStore.store);
  const initialAccountsStore = useUnit($initialAccountsStore.store);
  const initialCardsStore = useUnit($initialCardsStore.store);
  const initialCurrenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);

  const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false);
  const [isAuth, setIsAuth] = useState<boolean>(AuthUtils.isAuth());
  const hasInitialized = useRef<boolean>(false);
  const unsubscribeProfileWatch = useRef<(() => void) | null>(null);

  const sendTrackingInfoToSentry = () => {
    import('@shared/utils/trackUtils')
      .then(({ TrackUtils }) => TrackUtils.sendTrackingInfoToSentry())
      .catch((err) => console.error('Failed to send tracking info to Sentry', err));
  };
  const clearProfileWatch = () => {
    unsubscribeProfileWatch.current?.();
    unsubscribeProfileWatch.current = null;
  };

  const onCallbackAuthEvent = (isAuthTmp: boolean) => {
    setIsAuth(isAuthTmp);
    if (!isAuthTmp || hasInitialized.current) return;
    hasInitialized.current = true;
    Promise.allSettled([
      $profileStore.events.getProfile(),
      $profileFeatureStore.events.getProfileFeatures(),
      $initialAccountsStore.events.getAccounts(),
      $initialCardsStore.events.getCards(),
      $initialCurrenciesAvailableStore.events.getAvailableCurrencies(),
      $remoteConfigStore.events.getRemoteConfig(),
    ]);
  };

  useEffect(() => {
    onCallbackAuthEvent(isAuth);

    AuthUtils.eventIsAuth.on(AuthUtils.isAuthEventName, onCallbackAuthEvent);

    AuthUtils.init();

    if (!AuthUtils.isAuth()) {
      const userId = AuthUtils.getTempTokenForLogIn();
      const challenge = AuthUtils.getPkceChallengeForAuthenticate();
      if (userId && challenge) {
        setIsShowSpinner(true);
        AuthApi.authenticate({ tempToken: userId, challenge })
          .then((x) => {
            if (!x) return;
            AuthUtils.updateAuthData(x as LocalAuthData);
            AuthUtils.clearPkceChallenge();

            clearProfileWatch();
            unsubscribeProfileWatch.current = $profileStore.store.watch(({ data, error }) => {
              if (!data && !error) return;
              clearProfileWatch();
              sendTrackingInfoToSentry();
            });
          })
          .catch(() => AuthUtils.logout({ showNotification: true }))
          .finally(() => setIsShowSpinner(false));
      } else {
        AuthUtils.redirectToLoginPage();
      }
    } else {
      AuthUtils.eventIsAuth.emit(AuthUtils.isAuthEventName, true);
    }

    return () => {
      clearProfileWatch();
      AuthUtils.eventIsAuth.off(AuthUtils.isAuthEventName, onCallbackAuthEvent);
      hasInitialized.current = false;
    };
  }, []);

  if (isShowSpinner) return <Spinner position />;
  if (
    isAuth &&
    (profileFeatureStore.loading ||
      profileStore.loading ||
      remoteConfigStore.loading ||
      initialAccountsStore.loading ||
      initialCardsStore.loading ||
      initialCurrenciesAvailableStore.loading)
  )
    return <Spinner position />;
  if (isAuth)
    return (
      <Suspense fallback={<PageContainer />}>
        <Private />
      </Suspense>
    );
  return (
    <Suspense fallback={<></>}>
      <Public />
    </Suspense>
  );
};
